import { useMemo, useState, useEffect } from 'react';
import i18n from '../../i18n';
import { usePermissions } from '../../core/contexts/PermissionsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { HR_EMPLOYEES_REFRESH_EVENT, useHrApiRefreshListener } from '../ressources-humaines/hrRefreshEvents';

const ep = (key, opts) => i18n.t(`modules.employee_portal.pages.common.${key}`, {
  defaultValue: key === 'employee_scope_required'
    ? 'تعذّر تحديد الموظف — تأكد من ربط حسابك بسجل الموظف أو أعد تسجيل الدخول'
    : key === 'resolve_profile_failed'
      ? 'تعذر العثور على ملف الموظف لهذا الحساب'
      : undefined,
  ...opts,
});

/** Resolve portal employee context from permissions / local profile hints. */
export function usePortalEmployee() {
  const permissions = usePermissions();
  const currentUser = permissions?.currentUser;
  const [resolvedEmployeeId, setResolvedEmployeeId] = useState('');
  const [resolveError, setResolveError] = useState(null);
  const [profileResolved, setProfileResolved] = useState(false);
  const [lookupTick, setLookupTick] = useState(0);

  const base = useMemo(() => {
    let stored = null;
    let adminStored = null;
    try {
      const raw =
        localStorage.getItem('qawaem_user') ||
        localStorage.getItem('siterp_user') ||
        localStorage.getItem('user') ||
        localStorage.getItem('invoit_user');
      if (raw) stored = JSON.parse(raw);
      const adminRaw = localStorage.getItem('qawaem_admin_user');
      if (adminRaw) adminStored = JSON.parse(adminRaw);
    } catch (_) { /* ignore */ }

    const email =
      currentUser?.email ||
      stored?.email ||
      adminStored?.email ||
      (adminStored?.username && adminStored.username.includes('@') ? adminStored.username : '') ||
      '';
    const name =
      currentUser?.name ||
      currentUser?.username ||
      stored?.username ||
      stored?.name ||
      adminStored?.username ||
      adminStored?.name ||
      '';
    const employeeId =
      currentUser?.employeeId ||
      currentUser?.employee_id ||
      stored?.employeeId ||
      stored?.employee_id ||
      adminStored?.employeeId ||
      adminStored?.employee_id ||
      '';
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

    return { email, name, employeeId, isAdmin, currentUser };
  }, [currentUser]);

  useHrApiRefreshListener(HR_EMPLOYEES_REFRESH_EVENT, () => {
    setLookupTick((n) => n + 1);
  });

  useEffect(() => {
    if (base.employeeId) {
      setResolvedEmployeeId(String(base.employeeId));
      setResolveError(null);
      setProfileResolved(true);
      return;
    }
    const lookupEmail = base.email?.trim();
    if (!lookupEmail) {
      setProfileResolved(true);
      setResolvedEmployeeId('');
      setResolveError(null);
      return;
    }

    let cancelled = false;
    setProfileResolved(false);
    (async () => {
      try {
        const res = await localApiAdapter.get('/api/employee-portal/profile', {
          params: { email: lookupEmail },
        });
        const row = res && typeof res === 'object' && !Array.isArray(res) ? res : null;
        const id = row?.id;
        if (!cancelled && id != null) {
          setResolvedEmployeeId(String(id));
          setResolveError(null);
        } else if (!cancelled) {
          setResolvedEmployeeId('');
          setResolveError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[EmployeePortal] Employee ID lookup failed:', err?.message || err);
          setResolvedEmployeeId('');
          setResolveError(ep('resolve_profile_failed'));
        }
      } finally {
        if (!cancelled) setProfileResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [base.employeeId, base.email, lookupTick]);

  return {
    ...base,
    employeeId: base.employeeId || resolvedEmployeeId,
    resolveError,
    profileResolved,
    scopeReady: !!(base.employeeId || resolvedEmployeeId),
    refreshEmployeeScope: () => setLookupTick((n) => n + 1),
  };
}
